<script>
/* global axios */
import { mapGetters } from 'vuex';
import { useAlert } from 'dashboard/composables';
import ApiClient from 'dashboard/api/ApiClient';
import router from '../../../../index';
import PageHeader from '../../SettingsSubPageHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const datacrazyAPI = new ApiClient('datacrazy', { accountScoped: true });

export default {
  components: { PageHeader, NextButton },
  data() {
    return {
      apiKey: '',
      instances: [],
      instanceId: '',
      channelName: '',
      agentBotId: '',
      route: 'datacrazy',
      loadingInstances: false,
      creating: false,
    };
  },
  computed: {
    ...mapGetters({ agentBots: 'agentBots/getBots' }),
    accountId() {
      return this.$route.params.accountId;
    },
    selectedInstance() {
      return this.instances.find(i => i.id === this.instanceId) || null;
    },
  },
  mounted() {
    this.$store.dispatch('agentBots/get');
  },
  methods: {
    async loadInstances() {
      if (!this.apiKey.trim()) {
        useAlert('Cole a chave de API da DataCrazy.');
        return;
      }
      this.loadingInstances = true;
      try {
        const { data } = await axios.get(
          `/api/v1/accounts/${this.accountId}/datacrazy/instances`,
          { params: { api_key: this.apiKey.trim() } }
        );
        this.instances = data.instances || [];
        if (!this.instances.length) {
          useAlert('Nenhum número encontrado nessa conta da DataCrazy.');
          return;
        }
        const livre = this.instances.find(i => !i.in_use);
        this.instanceId = livre ? livre.id : this.instances[0].id;
        if (!this.channelName.trim() && livre) {
          this.channelName = `DataCrazy - ${livre.name || ''}`.trim();
        }
      } catch (error) {
        useAlert(
          error?.response?.data?.error ||
            'Não consegui buscar os números na DataCrazy.'
        );
      } finally {
        this.loadingInstances = false;
      }
    },
    async createChannel() {
      if (!this.channelName.trim()) {
        useAlert('Informe um nome para a caixa.');
        return;
      }
      if (!this.instanceId) {
        useAlert('Escolha o número da DataCrazy.');
        return;
      }
      this.creating = true;
      try {
        const { data } = await datacrazyAPI.create({
          name: this.channelName.trim(),
          api_key: this.apiKey.trim(),
          instance_id: this.instanceId,
          instance_name: this.selectedInstance?.name || '',
          instance_phone: this.selectedInstance?.phone || '',
          route: this.route,
          agent_bot_id: this.agentBotId || null,
        });
        router.replace({
          name: 'settings_inboxes_add_agents',
          params: { page: 'new', inbox_id: data.id },
        });
      } catch (error) {
        useAlert(
          error?.response?.data?.error || 'Não foi possível criar a caixa.'
        );
      } finally {
        this.creating = false;
      }
    },
  },
};
</script>

<template>
  <div class="h-full w-full p-6 col-span-6">
    <PageHeader
      header-title="DataCrazy"
      header-content="Traz para cá um número de WhatsApp que fica na DataCrazy. As conversas de lá aparecem nesta caixa e as respostas voltam para o cliente. Escolha a IA agora; agentes e times você adiciona nos próximos passos."
    />
    <form class="flex flex-wrap flex-col mx-0" @submit.prevent="createChannel()">
      <div class="flex-shrink-0 flex-grow-0">
        <label>
          Chave de API da DataCrazy
          <input
            v-model="apiKey"
            type="password"
            autocomplete="off"
            placeholder="Cole aqui a chave gerada no painel da DataCrazy"
          />
        </label>
        <p class="help-text">
          Painel da DataCrazy → Configurações → Chaves de API. Ela é exibida uma
          única vez, na criação.
        </p>
        <NextButton
          :is-loading="loadingInstances"
          type="button"
          faded
          blue
          label="Buscar meus números"
          @click="loadInstances"
        />
      </div>

      <div v-if="instances.length" class="flex-shrink-0 flex-grow-0 mt-4">
        <label>
          Número
          <select v-model="instanceId">
            <option
              v-for="inst in instances"
              :key="inst.id"
              :value="inst.id"
              :disabled="inst.in_use"
            >
              {{ inst.name }} — {{ inst.phone }}
              <template v-if="inst.in_use"> (já tem caixa)</template>
              <template v-else-if="!inst.connected">
                ({{ inst.status }})
              </template>
            </option>
          </select>
        </label>
      </div>

      <div v-if="instances.length" class="flex-shrink-0 flex-grow-0 mt-2">
        <label>
          Nome da caixa
          <input
            v-model="channelName"
            type="text"
            placeholder="Ex.: DataCrazy - Vendas"
          />
        </label>
      </div>

      <div v-if="instances.length" class="flex-shrink-0 flex-grow-0 mt-2">
        <label>
          IA (opcional)
          <select v-model="agentBotId">
            <option value="">Sem IA (só atendente humano)</option>
            <option v-for="bot in agentBots" :key="bot.id" :value="bot.id">
              {{ bot.name }}
            </option>
          </select>
        </label>
        <p class="help-text">
          A mesma IA das outras caixas. Dá para trocar depois, na aba Bot da
          caixa.
        </p>
      </div>

      <div v-if="instances.length" class="flex-shrink-0 flex-grow-0 mt-4">
        <label>Por onde as respostas saem</label>
        <label class="flex items-start gap-2 mt-1 font-normal cursor-pointer">
          <input v-model="route" type="radio" value="datacrazy" class="mt-1" />
          <span>
            <strong>Passando pela DataCrazy</strong> — tudo aparece no painel
            deles. O envio é só texto: anexo vira link e botões viram lista
            numerada.
          </span>
        </label>
        <label class="flex items-start gap-2 mt-2 font-normal cursor-pointer">
          <input v-model="route" type="radio" value="meta" class="mt-1" />
          <span>
            <strong>Direto pela Meta</strong> — imagem, áudio e botões de
            verdade, sem limite de envio. Em troca, o painel da DataCrazy não
            mostra as respostas.
          </span>
        </label>
        <p class="help-text">
          Pode trocar quando quiser, na aba DataCrazy da caixa.
        </p>
      </div>

      <div v-if="instances.length" class="w-full mt-4">
        <NextButton
          :is-loading="creating"
          type="submit"
          solid
          blue
          label="Criar caixa"
        />
      </div>
    </form>
  </div>
</template>
